/**
 * ============================================================================
 * USER / TENANT PAGE: Image Camp & Dynamic WhatsApp Campaign Hub
 * ============================================================================
 * Purpose:
 *   Visual wizard for creating and launching personalized Dynamic Image
 *   WhatsApp campaigns. Combines an Approved Meta WhatsApp Template (with
 *   an IMAGE header) and a saved Dynamic Canvas Template (designed in Dynamic
 *   Studio) to deliver auto-rendered personalized graphics for every recipient.
 * ============================================================================
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import dynamicImageService from "../../services/dynamicImageService";
import phoneNumberService from "../../services/phoneNumberService";
import campaignService from "../../services/campaignService";
import contactService from "../../services/contactService";
import * as XLSX from "xlsx";
import { generateDataUrl, replacePlaceholders, resolveImageUrl } from "./canvasRenderer";
import {
  SparklesIcon,
  PaperAirplaneIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  PhoneIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  EyeIcon,
  PlusIcon,
  TrashIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";

const ImageCampaign = ({ defaultTab = "create" }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTemplateId = searchParams.get("templateId");

  const [activeTab, setActiveTab] = useState(defaultTab); // "create" | "history"

  // Step 1: Campaign Details
  const [campaignName, setCampaignName] = useState(
    `Image Camp ${new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
  );
  const [senderNumbers, setSenderNumbers] = useState([]);
  const [selectedSenderId, setSelectedSenderId] = useState("");
  const [loadingSenders, setLoadingSenders] = useState(false);

  // Step 2A: Approved Meta WhatsApp Template (Must have IMAGE Header)
  const [metaTemplates, setMetaTemplates] = useState([]);
  const [selectedMetaTemplateName, setSelectedMetaTemplateName] = useState("");
  const [loadingMetaTemplates, setLoadingMetaTemplates] = useState(false);
  const [filterOnlyImageHeaders, setFilterOnlyImageHeaders] = useState(true);

  // Step 2B: Saved Dynamic Canvas Template (Designed in Studio)
  const [dynamicTemplates, setDynamicTemplates] = useState([]);
  const [selectedDynamicTemplateId, setSelectedDynamicTemplateId] = useState(initialTemplateId || "");
  const [loadingDynamicTemplates, setLoadingDynamicTemplates] = useState(false);

  // Step 3: Audience & Recipients
  const [audienceMode, setAudienceMode] = useState("manual"); // "manual" | "csv" | "group"
  const [manualText, setManualText] = useState("");
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [csvFileName, setCsvFileName] = useState("");
  const [recipients, setRecipients] = useState([]);

  // Excel / CSV Column to Variable Mapping State
  const [uploadedFileColumns, setUploadedFileColumns] = useState([]);
  const [uploadedRawRows, setUploadedRawRows] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});

  // Step 4: Schedule / Sending
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(0);

  // Live Sample Preview for selected recipient
  const [previewRecipientIdx, setPreviewRecipientIdx] = useState(0);
  const [samplePreviewUrl, setSamplePreviewUrl] = useState("");
  const [renderingPreview, setRenderingPreview] = useState(false);

  // Campaign History
  const [historyList, setHistoryList] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const fileInputRef = useRef(null);

  // Fetch initial dependencies on mount
  useEffect(() => {
    fetchSenderNumbers();
    fetchMetaTemplates();
    fetchDynamicTemplates();
    fetchGroups();
  }, []);

  useEffect(() => {
    if (activeTab === "history") {
      fetchCampaignHistory();
    }
  }, [activeTab]);

  // Filter approved Meta templates with IMAGE header ONLY
  const approvedImageMetaTemplates = useMemo(() => {
    return metaTemplates.filter((t) => {
      const headerType = (t.headerType || t.str_header_type || "").toUpperCase();
      const status = (t.status || t.str_status || "").toUpperCase();
      const isApproved = status === "APPROVED" || t.is_approved === true;
      if (!isApproved) return false;
      const isImageHeader =
        headerType === "IMAGE" ||
        t.raw?.obj_message?.components?.some(
          (c) => c.type === "HEADER" && (c.format === "IMAGE" || c.format === "image")
        ) ||
        t.components?.some(
          (c) => c.type === "HEADER" && (c.format === "IMAGE" || c.format === "image")
        );
      return isImageHeader;
    });
  }, [metaTemplates]);

  // Selected Meta Template Object
  const selectedMetaTemplate = useMemo(() => {
    return (
      approvedImageMetaTemplates.find((t) => t.name === selectedMetaTemplateName) ||
      approvedImageMetaTemplates[0] ||
      null
    );
  }, [approvedImageMetaTemplates, selectedMetaTemplateName]);

  // Auto-select first approved image template when loaded
  useEffect(() => {
    if (!selectedMetaTemplateName && approvedImageMetaTemplates.length > 0) {
      setSelectedMetaTemplateName(approvedImageMetaTemplates[0].name);
    }
  }, [approvedImageMetaTemplates, selectedMetaTemplateName]);

  // Selected Dynamic Canvas Template Object
  const selectedDynamicTemplate = useMemo(() => {
    return dynamicTemplates.find((t) => t._id === selectedDynamicTemplateId) || dynamicTemplates[0] || null;
  }, [dynamicTemplates, selectedDynamicTemplateId]);

  // Auto-select first dynamic canvas template when loaded
  useEffect(() => {
    if (!selectedDynamicTemplateId && dynamicTemplates.length > 0) {
      setSelectedDynamicTemplateId(dynamicTemplates[0]._id);
    }
  }, [dynamicTemplates, selectedDynamicTemplateId]);

  // 1. Meta WhatsApp Body Variables (must strictly match body text placeholders, e.g. {{1}}, {{2}})
  const metaBodyVariables = useMemo(() => {
    if (!selectedMetaTemplate) return [];
    if (selectedMetaTemplate.bodyText) {
      const matches = [...selectedMetaTemplate.bodyText.matchAll(/\{\{([^}]+)\}\}/g)];
      return matches.map((m) => m[1].trim());
    }
    if (selectedMetaTemplate.variables && Array.isArray(selectedMetaTemplate.variables)) {
      return selectedMetaTemplate.variables
        .map((v) => v.replace(/[\{\}\s]/g, ""))
        .filter(Boolean);
    }
    return [];
  }, [selectedMetaTemplate]);

  // 2. Canvas Image Graphic Variables (from canvas layers and template config)
  const canvasImageVariables = useMemo(() => {
    const set = new Set();
    if (selectedDynamicTemplate?.arr_variables) {
      selectedDynamicTemplate.arr_variables.forEach((v) => set.add(v));
    }
    const layers = selectedDynamicTemplate?.layers || [];
    layers.forEach((l) => {
      if (l.type === "text" && l.text) {
        const matches = [...l.text.matchAll(/\{\{([^}]+)\}\}/g)];
        matches.forEach((m) => set.add(m[1].trim()));
      }
    });
    return Array.from(set);
  }, [selectedDynamicTemplate]);

  // 3. Combined Ordered Variable Columns for Audience Input:
  // Meta Body variables first, followed by Canvas Graphic variables
  const audienceVariableColumns = useMemo(() => {
    const cols = [];
    metaBodyVariables.forEach((mv) => {
      cols.push({ key: mv, source: "meta", label: `Body: {{${mv}}}` });
    });
    canvasImageVariables.forEach((cv) => {
      if (!metaBodyVariables.includes(cv)) {
        cols.push({ key: cv, source: "canvas", label: `Graphic: {{${cv}}}` });
      }
    });
    return cols;
  }, [metaBodyVariables, canvasImageVariables]);

  const templateVariables = useMemo(() => {
    return audienceVariableColumns.map((c) => c.key);
  }, [audienceVariableColumns]);

  // Fetch sender phone numbers
  const fetchSenderNumbers = async () => {
    try {
      setLoadingSenders(true);
      const res = await phoneNumberService.getPhoneNumbers();
      const list = res?.data || res?.data?.data || (Array.isArray(res) ? res : []);
      setSenderNumbers(list);
      if (list.length > 0 && !selectedSenderId) {
        setSelectedSenderId(list[0].str_phone_number_id || list[0]._id || list[0].str_phone_number);
      }
    } catch (err) {
      console.error("Failed to load sender numbers:", err);
      toast.error("Could not load sender phone numbers");
    } finally {
      setLoadingSenders(false);
    }
  };

  // Fetch approved WhatsApp Meta templates
  const fetchMetaTemplates = async () => {
    try {
      setLoadingMetaTemplates(true);
      const res = await campaignService.getUserTemplates({ all: "true" });
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.templates)
        ? res.templates
        : Array.isArray(res)
        ? res
        : [];
      setMetaTemplates(list);
    } catch (err) {
      console.error("Failed to load Meta templates:", err);
    } finally {
      setLoadingMetaTemplates(false);
    }
  };

  // Fetch saved dynamic canvas templates (from Dynamic Image Studio)
  const fetchDynamicTemplates = async () => {
    try {
      setLoadingDynamicTemplates(true);
      const res = await dynamicImageService.getTemplates();
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res)
        ? res
        : [];
      setDynamicTemplates(list);
      if (!selectedDynamicTemplateId && list.length > 0) {
        setSelectedDynamicTemplateId(list[0]._id);
      }
    } catch (err) {
      console.error("Failed to load dynamic canvas templates:", err);
    } finally {
      setLoadingDynamicTemplates(false);
    }
  };

  // Fetch contact groups
  const fetchGroups = async () => {
    try {
      const res = await contactService.groups.get();
      const list = res?.data || res?.data?.data || (Array.isArray(res) ? res : []);
      setGroups(list);
    } catch (_) {}
  };

  // Parse audience when manual mode or text changes
  useEffect(() => {
    if (audienceMode === "manual") {
      parseManualText(manualText);
    }
  }, [manualText, audienceMode, audienceVariableColumns]);

  const parseManualText = (text) => {
    const lines = text
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);

    const parsed = lines.map((line, idx) => {
      const parts = line.split(",").map((p) => p.trim());
      const phone = parts[0]?.replace(/[^0-9]/g, "") || "";
      const metaVars = {};
      const imageVars = {};
      const combinedVars = {};

      audienceVariableColumns.forEach((col, cIdx) => {
        const val =
          parts[cIdx + 1] !== undefined && parts[cIdx + 1] !== ""
            ? parts[cIdx + 1]
            : col.key === "name" || col.key === "1"
            ? `User ${idx + 1}`
            : `Sample ${col.key}`;

        combinedVars[col.key] = val;
        if (col.source === "meta") {
          metaVars[col.key] = val;
        } else {
          imageVars[col.key] = val;
        }
      });

      // Ensure canvas variables include any variables referenced across layers
      canvasImageVariables.forEach((cv) => {
        if (!imageVars[cv] && combinedVars[cv]) {
          imageVars[cv] = combinedVars[cv];
        }
      });

      return {
        id: `rec_${idx}`,
        phone,
        name: metaVars["1"] || metaVars.name || combinedVars.name || parts[1] || `Recipient ${idx + 1}`,
        variables: metaVars, // STRICTLY Meta body variables!
        template_variables: metaVars,
        image_variables: imageVars, // STRICTLY Canvas graphic variables!
        all_variables: combinedVars,
      };
    });

    setRecipients(parsed);
    if (parsed.length > 0 && previewRecipientIdx >= parsed.length) {
      setPreviewRecipientIdx(0);
    }
  };

  // Format any cell value safely (handles numbers, scientific notation, strings, empty)
  const formatCell = (val) => {
    if (val === null || val === undefined) return "";
    if (typeof val === "number") {
      if (Number.isInteger(val) || Math.abs(val) >= 1e9) {
        if (typeof window !== "undefined" && typeof window.BigInt === "function") {
          return window.BigInt(Math.round(val)).toString();
        }
        return Math.round(val).toFixed(0);
      }
      return String(val);
    }
    const str = String(val).trim();
    if (/^[+-]?\d+(\.\d+)?[eE][+-]?\d+$/.test(str)) {
      try {
        const num = Number(str);
        if (typeof window !== "undefined" && typeof window.BigInt === "function") {
          return window.BigInt(Math.round(num)).toString();
        }
        return Math.round(num).toFixed(0);
      } catch (_) {
        return str;
      }
    }
    return str;
  };

  // Sample row values from uploaded Excel / CSV for dropdown preview
  const sampleRow = useMemo(() => {
    if (!uploadedFileColumns || uploadedFileColumns.length === 0 || !uploadedRawRows || uploadedRawRows.length === 0) {
      return {};
    }
    const firstRow = uploadedRawRows[0] || [];
    const sample = {};
    uploadedFileColumns.forEach((col, idx) => {
      sample[col] = formatCell(firstRow[idx]);
    });
    return sample;
  }, [uploadedFileColumns, uploadedRawRows]);

  // Auto-detect variable to column mapping
  const autoDetectMapping = (columns, existingMapping = {}) => {
    const rawHeaders = columns || [];
    const normalizedHeaders = rawHeaders.map((h) =>
      h.toLowerCase().replace(/[{}\s_-]/g, "")
    );

    const mapping = { ...existingMapping };

    // 1. Phone Column
    if (!mapping.phone || !rawHeaders.includes(mapping.phone)) {
      let pIdx = normalizedHeaders.findIndex((h) =>
        ["phone", "mobile", "whatsapp", "contact"].includes(h)
      );
      if (pIdx === -1) {
        const hasNumberGraphicVar = canvasImageVariables.some(
          (cv) => cv.toLowerCase() === "number"
        );
        if (!hasNumberGraphicVar) {
          pIdx = normalizedHeaders.findIndex((h) => h === "number");
        }
      }
      if (pIdx === -1) pIdx = 0;
      mapping.phone = rawHeaders[pIdx] || "";
    }

    // 2. Name Column (Optional)
    if (!mapping.name || !rawHeaders.includes(mapping.name)) {
      const nIdx = normalizedHeaders.findIndex((h) =>
        ["name", "customername", "firstname", "fullname"].includes(h)
      );
      if (nIdx !== -1) {
        mapping.name = rawHeaders[nIdx];
      }
    }

    // 3. Meta Body variables (e.g. {{1}}, {{2}})
    metaBodyVariables.forEach((mv, mIdx) => {
      if (!mapping[mv] || !rawHeaders.includes(mapping[mv])) {
        const keyNorm = String(mv).toLowerCase().replace(/[{}\s_-]/g, "");
        const candidates = [
          keyNorm,
          `var${keyNorm}`,
          `param${keyNorm}`,
          `variable${keyNorm}`,
          `body${keyNorm}`,
          `col${keyNorm}`,
        ];
        let foundCol = "";
        for (const cand of candidates) {
          const idx = normalizedHeaders.indexOf(cand);
          if (idx !== -1) {
            foundCol = rawHeaders[idx];
            break;
          }
        }
        if (!foundCol) {
          const posIdx = mIdx + 1;
          if (posIdx < rawHeaders.length) {
            foundCol = rawHeaders[posIdx];
          }
        }
        mapping[mv] = foundCol || "";
      }
    });

    // 4. Canvas graphic variables (e.g. {{name}}, {{offer}}, {{code}}, {{number}})
    canvasImageVariables.forEach((cv, cIdx) => {
      if (!mapping[cv] || !rawHeaders.includes(mapping[cv])) {
        const keyNorm = String(cv).toLowerCase().replace(/[{}\s_-]/g, "");
        const candidates = [
          keyNorm,
          `var${keyNorm}`,
          `param${keyNorm}`,
          `variable${keyNorm}`,
          `col${keyNorm}`,
        ];
        let foundCol = "";
        for (const cand of candidates) {
          const idx = normalizedHeaders.indexOf(cand);
          if (idx !== -1) {
            foundCol = rawHeaders[idx];
            break;
          }
        }
        if (!foundCol) {
          const posIdx = metaBodyVariables.length + cIdx + 1;
          if (posIdx < rawHeaders.length) {
            foundCol = rawHeaders[posIdx];
          }
        }
        mapping[cv] = foundCol || "";
      }
    });

    return mapping;
  };

  // Re-map rows whenever column mapping changes
  const applyColumnMapping = (mapping, columns, rawRows) => {
    if (!columns || columns.length === 0 || !rawRows || rawRows.length === 0) {
      setRecipients([]);
      return [];
    }

    const phoneColName = mapping.phone || "";
    const phoneColIdx = columns.indexOf(phoneColName);
    const nameColName = mapping.name || "";
    const nameColIdx = columns.indexOf(nameColName);

    const parsed = [];

    for (let i = 0; i < rawRows.length; i++) {
      const row = rawRows[i] || [];
      const hasAny = row.some((c) => formatCell(c) !== "");
      if (!hasAny) continue;

      const rawPhone = formatCell(phoneColIdx >= 0 ? row[phoneColIdx] : row[0]);
      const phone = rawPhone.replace(/[^0-9]/g, "");
      if (!phone || phone.length < 7) continue;

      const metaVars = {};
      const imageVars = {};
      const combinedVars = {};

      // Map Meta body variables
      metaBodyVariables.forEach((mv) => {
        const colName = mapping[mv];
        const colIdx = colName ? columns.indexOf(colName) : -1;
        const val = colIdx >= 0 ? formatCell(row[colIdx]) : "";
        metaVars[mv] = val;
        combinedVars[mv] = val;
      });

      // Map Canvas graphic variables
      canvasImageVariables.forEach((cv) => {
        const colName = mapping[cv];
        const colIdx = colName ? columns.indexOf(colName) : -1;
        const val = colIdx >= 0 ? formatCell(row[colIdx]) : (combinedVars[cv] || "");
        imageVars[cv] = val;
        combinedVars[cv] = val;
      });

      const customerName =
        nameColIdx >= 0 && formatCell(row[nameColIdx])
          ? formatCell(row[nameColIdx])
          : combinedVars.name || metaVars["1"] || `Recipient ${i + 1}`;

      parsed.push({
        id: `rec_${i}_${Date.now()}`,
        phone,
        name: customerName,
        variables: metaVars, // Strictly Meta body variables!
        template_variables: metaVars,
        image_variables: imageVars, // Strictly Canvas graphic variables!
        all_variables: combinedVars,
      });
    }

    setRecipients(parsed);
    if (parsed.length > 0 && previewRecipientIdx >= parsed.length) {
      setPreviewRecipientIdx(0);
    }
    return parsed;
  };

  // Change mapping for a specific variable
  const handleMappingChange = (varKey, selectedCol) => {
    const updated = {
      ...columnMapping,
      [varKey]: selectedCol,
    };
    setColumnMapping(updated);
    applyColumnMapping(updated, uploadedFileColumns, uploadedRawRows);
  };

  // Automatically sync mappings if meta/canvas template variables change while file is loaded
  useEffect(() => {
    if (audienceMode === "csv" && uploadedFileColumns.length > 0 && uploadedRawRows.length > 0) {
      const autoMap = autoDetectMapping(uploadedFileColumns, columnMapping);
      setColumnMapping(autoMap);
      applyColumnMapping(autoMap, uploadedFileColumns, uploadedRawRows);
    }
  }, [metaBodyVariables, canvasImageVariables]);

  // Handle CSV and Excel (.xlsx, .xls) file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCsvFileName(file.name);
    const fileExt = file.name.split(".").pop().toLowerCase();

    try {
      let rowsAoa = [];

      if (fileExt === "xlsx" || fileExt === "xls") {
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
          toast.error("The Excel workbook contains no sheets");
          return;
        }
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        rowsAoa = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
          raw: true,
        });
      } else {
        // CSV / TXT
        const text = await file.text();
        try {
          const workbook = XLSX.read(text, { type: "string" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          rowsAoa = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: "",
            raw: true,
          });
        } catch (_) {
          rowsAoa = text
            .split(/\r\n|\n/)
            .map((l) => l.trim())
            .filter(Boolean)
            .map((l) => l.split(",").map((c) => c.replace(/^["']|["']$/g, "").trim()));
        }
      }

      if (!rowsAoa || rowsAoa.length < 2) {
        toast.error("The uploaded file has no data rows");
        return;
      }

      // Header row & Data rows
      const rawHeaders = (rowsAoa[0] || []).map((h) => formatCell(h));
      const rawRows = rowsAoa.slice(1);

      setUploadedFileColumns(rawHeaders);
      setUploadedRawRows(rawRows);

      const autoMap = autoDetectMapping(rawHeaders, {});
      setColumnMapping(autoMap);
      const parsed = applyColumnMapping(autoMap, rawHeaders, rawRows);

      toast.success(
        `Loaded ${parsed.length} recipients! Check variable column mappings below.`
      );
    } catch (err) {
      console.error("Failed to parse file:", err);
      toast.error(`Error reading file: ${err.message || "Invalid file format"}`);
    } finally {
      if (e.target) e.target.value = "";
    }
  };

  // Re-render sample dynamic canvas preview whenever selected template or preview recipient changes
  useEffect(() => {
    renderSampleRecipientPreview();
  }, [selectedDynamicTemplate, recipients, previewRecipientIdx]);

  const renderSampleRecipientPreview = async () => {
    if (!selectedDynamicTemplate) return;
    const currentRec = recipients[previewRecipientIdx] || {
      all_variables: { name: "Alex Morgan", offer: "30% OFF", code: "SAVE30", "1": "124", var1: "Nikhil", address: "Ahmedabad" },
    };

    const varsForCanvas = currentRec.all_variables || {
      ...(currentRec.template_variables || {}),
      ...(currentRec.image_variables || {}),
      ...(currentRec.variables || {}),
    };

    try {
      setRenderingPreview(true);
      const dataUrl = await generateDataUrl(
        {
          canvas: selectedDynamicTemplate.canvas || {
            width: selectedDynamicTemplate.num_width || 1080,
            height: selectedDynamicTemplate.num_height || 1080,
            backgroundColor: selectedDynamicTemplate.str_bg_color || "#1e293b",
            backgroundImage: selectedDynamicTemplate.str_bg_image_url || "",
          },
          layers: selectedDynamicTemplate.layers || [],
        },
        varsForCanvas
      );
      setSamplePreviewUrl(dataUrl);
    } catch (err) {
      console.error("Preview render failed:", err);
    } finally {
      setRenderingPreview(false);
    }
  };

  // Fetch past campaigns
  const fetchCampaignHistory = async () => {
    try {
      setLoadingHistory(true);
      const res = await campaignService.getCampaigns({ limit: 50 });
      const list = res?.data || res?.data?.data || (Array.isArray(res) ? res : []);
      const dynList = list.filter(
        (c) =>
          c.obj_template_data?.dynamic_image ||
          c.str_campaign_id?.startsWith("DYN_") ||
          c.str_campaign_name?.toLowerCase().includes("image")
      );
      setHistoryList(dynList.length > 0 ? dynList : list.slice(0, 20));
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Launch Dynamic Campaign
  const handleLaunchCampaign = async () => {
    if (!campaignName.trim()) {
      toast.error("Please provide a campaign name");
      return;
    }
    if (!selectedSenderId) {
      toast.error("Please select a sending WhatsApp phone number");
      return;
    }
    if (!selectedMetaTemplate) {
      toast.error("Please select an approved WhatsApp Meta template with an IMAGE header");
      return;
    }
    if (!selectedDynamicTemplate) {
      toast.error("Please select a saved dynamic canvas template");
      return;
    }
    if (recipients.length === 0) {
      toast.error("No valid recipients found. Please upload a file and check your column mapping.");
      return;
    }
    if (audienceMode === "csv" && !columnMapping.phone) {
      toast.error("Please match the Recipient Phone Number column in the variable mapping section.");
      return;
    }

    const confirm = await Swal.fire({
      title: "Launch Image Camp?",
      html: `
        <div class="text-left text-xs space-y-2 mt-2 bg-gray-50 dark:bg-gray-800 p-3.5 rounded-xl border border-gray-200 dark:border-gray-700">
          <p><strong>Campaign Name:</strong> ${campaignName}</p>
          <p><strong>Meta WhatsApp Template:</strong> <span class="text-blue-600 font-mono font-bold">${selectedMetaTemplate.name}</span> (Approved Image Header)</p>
          <p><strong>Canvas Design:</strong> ${selectedDynamicTemplate.str_title || selectedDynamicTemplate.str_template_name}</p>
          <p><strong>Total Recipients:</strong> ${recipients.length}</p>
          <p><strong>Dispatch:</strong> ${isScheduled ? `Scheduled for ${scheduledAt}` : "Instant Dispatch"}</p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isScheduled ? "Schedule Campaign" : "Send Now",
      confirmButtonColor: "#3a57e8",
      cancelButtonColor: "#64748b",
    });

    if (!confirm.isConfirmed) return;

    try {
      setIsSending(true);
      setSendProgress(10);

      // Pre-render recipient images in browser
      const processed = [];
      const total = recipients.length;

      for (let i = 0; i < total; i++) {
        const r = recipients[i];
        let imageBase64 = "";

        const varsForCanvas = r.all_variables || {
          ...(r.template_variables || {}),
          ...(r.image_variables || {}),
          ...(r.variables || {}),
        };

        // Render dynamic image on-the-fly
        if (i < 50) {
          try {
            imageBase64 = await generateDataUrl(
              {
                canvas: selectedDynamicTemplate.canvas || {
                  width: selectedDynamicTemplate.num_width || 1080,
                  height: selectedDynamicTemplate.num_height || 1080,
                  backgroundColor: selectedDynamicTemplate.str_bg_color || "#1e293b",
                  backgroundImage: selectedDynamicTemplate.str_bg_image_url || "",
                },
                layers: selectedDynamicTemplate.layers || [],
              },
              varsForCanvas
            );
          } catch (_) {}
        }

        processed.push({
          phone: r.phone,
          name: r.name,
          variables: r.template_variables || r.variables || {}, // Strictly Meta body variables!
          image_variables: r.image_variables || {}, // Strictly Canvas image variables!
          imageBase64,
        });

        if (i % 5 === 0) {
          setSendProgress(10 + Math.round((i / total) * 70));
        }
      }

      setSendProgress(85);

      const payload = {
        str_campaign_name: campaignName.trim(),
        str_phone_number_id: selectedSenderId,
        // Approved Meta Template Name (required by Karix & WhatsApp Cloud API)
        str_template_name: selectedMetaTemplate.name,
        int_template_id: selectedMetaTemplate.id,
        str_campaign_type: selectedMetaTemplate.category || "MARKETING",
        // Dynamic Canvas Design Details
        str_dynamic_template_name:
          selectedDynamicTemplate.str_title || selectedDynamicTemplate.str_template_name,
        obj_dynamic_image_config: selectedDynamicTemplate,
        obj_template_data: {
          templateId: selectedMetaTemplate.id,
          name: selectedMetaTemplate.name,
          category: selectedMetaTemplate.category || "MARKETING",
          headerType: "IMAGE",
          language: selectedMetaTemplate.language || "en",
          bodyText: selectedMetaTemplate.bodyText || "",
          footerText: selectedMetaTemplate.footerText || "",
          buttons: selectedMetaTemplate.buttons || [],
          dynamic_image: true,
          dynamic_template_id: selectedDynamicTemplate._id,
          dynamic_template_name:
            selectedDynamicTemplate.str_title || selectedDynamicTemplate.str_template_name,
          config: selectedDynamicTemplate,
        },
        arr_recipients: processed,
        obj_variable_mapping: columnMapping,
        obj_column_mapping: columnMapping,
        dttm_scheduled_at: isScheduled && scheduledAt ? scheduledAt : null,
      };

      const res = await dynamicImageService.sendDynamicCampaign(payload);
      setSendProgress(100);

      if (res?.success || res?.data?.success) {
        await Swal.fire({
          title: "🚀 Image Campaign Launched!",
          text: `Your dynamic image campaign "${campaignName}" using Meta template "${selectedMetaTemplate.name}" has been dispatched with ${recipients.length} personalized messages.`,
          icon: "success",
          confirmButtonColor: "#3a57e8",
        });

        setActiveTab("history");
        fetchCampaignHistory();
      }
    } catch (err) {
      console.error("Campaign Launch Error:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to launch dynamic image campaign");
    } finally {
      setIsSending(false);
      setSendProgress(0);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f6fa] dark:bg-[#0f172a] space-y-6">
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white dark:bg-[#151824] p-5 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-xs">
        <div className="flex items-center gap-3">
          <span className="p-2.5 rounded-xl bg-gradient-to-tr from-[#3a57e8] to-blue-400 text-white shadow-md shadow-[#3a57e8]/30">
            <SparklesIcon className="w-6 h-6" />
          </span>
          <div>
            <h1 className="text-xl font-bold text-[#232d42] dark:text-white flex items-center gap-2">
              Image Camp Studio
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-500 uppercase tracking-wide">
                Personalized Graphics
              </span>
            </h1>
            <p className="text-xs text-[#8a92a6] dark:text-gray-400 mt-0.5">
              Deliver personalized dynamic images rendered for each recipient via Approved Meta WhatsApp Image Templates
            </p>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center bg-gray-100 dark:bg-gray-800 p-1 rounded-xl">
          <button
            onClick={() => setActiveTab("create")}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition ${
              activeTab === "create"
                ? "bg-white dark:bg-gray-700 text-[#3a57e8] shadow-xs"
                : "text-gray-500 hover:text-gray-900 dark:hover:text-white"
            }`}
          >
            New Campaign
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`px-4 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition ${
              activeTab === "history"
                ? "bg-white dark:bg-gray-700 text-[#3a57e8] shadow-xs"
                : "text-gray-500 hover:text-gray-900 dark:hover:text-white"
            }`}
          >
            <ClockIcon className="w-4 h-4" />
            <span>Campaign History</span>
          </button>
        </div>
      </div>

      {/* CREATE TAB: Campaign Flow Wizard */}
      {activeTab === "create" ? (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* LEFT COLUMN: Configuration Steps (7 cols) */}
          <div className="lg:col-span-7 space-y-5">
            {/* STEP 1: Basic Campaign Info */}
            <div className="bg-white dark:bg-[#151824] p-5 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-xs space-y-4">
              <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
                <span className="w-5 h-5 rounded-full bg-[#3a57e8] text-white flex items-center justify-center text-[10px]">
                  1
                </span>
                <span>Campaign Name & WhatsApp Sender</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1.5">
                    Campaign Name
                  </label>
                  <input
                    type="text"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-xs text-gray-900 dark:text-white focus:outline-none focus:border-[#3a57e8]"
                    placeholder="e.g. Festive Offer Blast"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1.5">
                    Sending Phone Number
                  </label>
                  <select
                    value={selectedSenderId}
                    onChange={(e) => setSelectedSenderId(e.target.value)}
                    disabled={loadingSenders || senderNumbers.length === 0}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-xs text-gray-900 dark:text-white focus:outline-none focus:border-[#3a57e8]"
                  >
                    {senderNumbers.length === 0 ? (
                      <option value="">No provisioned phone numbers</option>
                    ) : (
                      senderNumbers.map((s) => (
                        <option
                          key={s._id || s.str_phone_number_id}
                          value={s.str_phone_number_id || s._id}
                        >
                          {s.str_phone_number || s.str_display_phone_number || s.str_phone_number_id}{" "}
                          ({s.str_operator || "Meta"})
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>
            </div>

            {/* STEP 2A: APPROVED META WHATSAPP TEMPLATE (Must have Image Header) */}
            <div className="bg-white dark:bg-[#151824] p-5 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-xs space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
                  <span className="w-5 h-5 rounded-full bg-[#3a57e8] text-white flex items-center justify-center text-[10px]">
                    2A
                  </span>
                  <span>Approved Meta WhatsApp Template (Image Header)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600">
                    Approved Image Header Only
                  </span>
                </div>
              </div>

              {loadingMetaTemplates ? (
                <div className="py-6 text-center text-xs text-gray-400">Loading Meta templates...</div>
              ) : approvedImageMetaTemplates.length === 0 ? (
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-xl space-y-2">
                  <div className="flex items-start gap-2 text-amber-800 dark:text-amber-300 text-xs font-medium">
                    <InformationCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      No approved Meta templates with an <strong>IMAGE</strong> header found.
                    </span>
                  </div>
                  <p className="text-[11px] text-amber-700/80 dark:text-amber-400/80 pl-6">
                    WhatsApp Cloud API requires dynamic graphics to be sent as the Image Header of an approved Meta template. Please ensure you have an approved template with an IMAGE header in Template Management.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1.5">
                      Select Approved Meta Template
                    </label>
                    <select
                      value={selectedMetaTemplateName}
                      onChange={(e) => setSelectedMetaTemplateName(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-xs font-bold text-gray-900 dark:text-white focus:outline-none focus:border-[#3a57e8]"
                    >
                      {approvedImageMetaTemplates.map((t) => (
                        <option key={t.id || t.name} value={t.name}>
                          {t.name} • {t.category} • [{t.headerType || "IMAGE"}] • {t.language}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Selected Meta Template Specs */}
                  {selectedMetaTemplate && (
                    <div className="p-3 bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-xl space-y-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-bold text-blue-600 dark:text-blue-400">
                          {selectedMetaTemplate.name}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 font-bold uppercase">
                            {selectedMetaTemplate.status || "APPROVED"}
                          </span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-600 font-bold uppercase">
                            Header: {selectedMetaTemplate.headerType || "IMAGE"}
                          </span>
                        </div>
                      </div>
                      <p className="text-[11px] text-gray-600 dark:text-gray-300 line-clamp-2 italic">
                        "{selectedMetaTemplate.bodyText || "No body text"}"
                      </p>
                      {metaBodyVariables.length > 0 && (
                        <div className="flex items-center gap-1.5 pt-1 text-[10px]">
                          <span className="text-gray-500">Body Parameters ({metaBodyVariables.length}):</span>
                          {metaBodyVariables.map((v) => (
                            <span key={v} className="bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded font-mono font-semibold">
                              {"{{" + v + "}}"}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* STEP 2B: SAVED DYNAMIC CANVAS TEMPLATE (Visual Designer) */}
            <div className="bg-white dark:bg-[#151824] p-5 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-xs space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
                  <span className="w-5 h-5 rounded-full bg-[#3a57e8] text-white flex items-center justify-center text-[10px]">
                    2B
                  </span>
                  <span>Select Saved Dynamic Image Template (Canvas Design)</span>
                </div>
                <button
                  onClick={() => navigate("/workspace/dynamic-images/create")}
                  className="text-xs font-semibold text-[#3a57e8] hover:underline flex items-center gap-1"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  <span>Create in Studio</span>
                </button>
              </div>

              {loadingDynamicTemplates ? (
                <div className="py-6 text-center text-xs text-gray-400">Loading dynamic designs...</div>
              ) : dynamicTemplates.length === 0 ? (
                <div className="p-6 text-center border border-dashed border-gray-200 dark:border-gray-800 rounded-xl">
                  <p className="text-xs text-gray-500 mb-2">No dynamic image templates created yet.</p>
                  <button
                    onClick={() => navigate("/workspace/dynamic-images/create")}
                    className="px-3.5 py-1.5 rounded-lg bg-[#3a57e8] text-white text-xs font-semibold"
                  >
                    Open Dynamic Studio
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1.5">
                      Choose Saved Canvas Design
                    </label>
                    <select
                      value={selectedDynamicTemplateId}
                      onChange={(e) => setSelectedDynamicTemplateId(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-xs font-bold text-gray-900 dark:text-white focus:outline-none focus:border-[#3a57e8]"
                    >
                      {dynamicTemplates.map((t) => (
                        <option key={t._id} value={t._id}>
                          {t.str_title || t.str_template_name} ({t.canvas?.width || 1080}x{t.canvas?.height || 1080}px) • {(t.arr_variables || []).length} graphic vars
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-56 overflow-y-auto p-1">
                    {dynamicTemplates.map((tmpl) => {
                      const isSelected = tmpl._id === selectedDynamicTemplateId;
                      const thumb =
                        tmpl.preview_thumbnail ||
                        tmpl.str_preview_image_url ||
                        tmpl.canvas?.backgroundImage ||
                        tmpl.str_bg_image_url ||
                        "";
                      return (
                        <div
                          key={tmpl._id}
                          onClick={() => setSelectedDynamicTemplateId(tmpl._id)}
                          className={`p-2.5 rounded-xl border cursor-pointer transition flex flex-col gap-2 relative ${
                            isSelected
                              ? "border-[#3a57e8] bg-blue-50/40 dark:bg-blue-900/20 shadow-xs"
                              : "border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50"
                          }`}
                        >
                          {isSelected && (
                            <div className="absolute top-2 right-2 w-4 h-4 bg-[#3a57e8] text-white rounded-full flex items-center justify-center text-[10px] shadow">
                              ✓
                            </div>
                          )}
                          <div className="aspect-video w-full rounded-lg bg-slate-900 overflow-hidden flex items-center justify-center">
                            {thumb ? (
                              <img
                                src={resolveImageUrl(thumb)}
                                alt="Preview"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  const cur = e.target.src || "";
                                  if (cur.includes("apis.digiforest.in")) {
                                    e.target.src = cur.replace("https://apis.digiforest.in", "http://localhost:8005");
                                  } else {
                                    e.target.style.display = "none";
                                  }
                                }}
                              />
                            ) : (
                              <span className="text-xs text-slate-500">🖼️</span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-xs font-bold text-gray-900 dark:text-white truncate">
                              {tmpl.str_title || tmpl.str_template_name}
                            </h4>
                            <div className="flex items-center gap-1 mt-1 text-[10px] text-gray-400">
                              <span>{tmpl.canvas?.width || 1080}px</span>
                              <span>•</span>
                              <span>{(tmpl.arr_variables || []).length} vars</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* STEP 3: Audience & Recipients */}
            <div className="bg-white dark:bg-[#151824] p-5 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-xs space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
                  <span className="w-5 h-5 rounded-full bg-[#3a57e8] text-white flex items-center justify-center text-[10px]">
                    3
                  </span>
                  <span>Audience & Variable Routing</span>
                </div>
                <span className="text-xs font-bold text-[#3a57e8]">
                  {recipients.length} Recipient{recipients.length === 1 ? "" : "s"} Ready
                </span>
              </div>

              {/* Variable Legends / Helper */}
              <div className="p-3 bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800 rounded-xl space-y-2">
                <div className="text-[11px] font-semibold text-gray-600 dark:text-gray-400">
                  Parameter Routing Format ({audienceVariableColumns.length} Total Variables):
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-semibold">
                    Col 1: Phone
                  </span>
                  {audienceVariableColumns.map((col, idx) => (
                    <span
                      key={col.key}
                      className={`text-[10px] font-mono px-2 py-0.5 rounded font-semibold ${
                        col.source === "meta"
                          ? "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
                          : "bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800"
                      }`}
                      title={col.source === "meta" ? "Sent to WhatsApp Meta Template Body" : "Rendered on Canvas Graphic"}
                    >
                      Col {idx + 2}: {"{{" + col.key + "}}"} {col.source === "meta" ? "[WhatsApp Body]" : "[Image Graphic]"}
                    </span>
                  ))}
                </div>
              </div>

              {/* Mode Tabs */}
              <div className="flex items-center gap-2 border-b border-gray-100 dark:border-gray-800 pb-2">
                <button
                  onClick={() => setAudienceMode("manual")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                    audienceMode === "manual"
                      ? "bg-[#3a57e8] text-white"
                      : "text-gray-500 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  Quick Manual Input
                </button>
                <button
                  onClick={() => setAudienceMode("csv")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                    audienceMode === "csv"
                      ? "bg-[#3a57e8] text-white"
                      : "text-gray-500 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  Upload CSV / Excel
                </button>
                <button
                  onClick={() => setAudienceMode("group")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                    audienceMode === "group"
                      ? "bg-[#3a57e8] text-white"
                      : "text-gray-500 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  Contact Group
                </button>
              </div>

              {/* Manual Input */}
              {audienceMode === "manual" && (
                <div className="space-y-2">
                  <label className="text-[11px] text-gray-500 block">
                    Enter one recipient per line in format:{" "}
                    <code className="text-[#3a57e8] font-bold">
                      phone{audienceVariableColumns.length > 0 ? `, ${audienceVariableColumns.map((c) => c.key).join(", ")}` : ""}
                    </code>
                  </label>
                  <textarea
                    rows={4}
                    value={manualText}
                    onChange={(e) => setManualText(e.target.value)}
                    placeholder={`919166344811${audienceVariableColumns.length > 0 ? ", " + audienceVariableColumns.map((c) => (c.source === "meta" ? "124" : "Nikhil")).join(", ") : ""}`}
                    className="w-full px-3 py-2 text-xs font-mono rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-[#3a57e8]"
                  />
                </div>
              )}

              {/* CSV / Excel Upload & Variable Mapping */}
              {audienceMode === "csv" && (
                <div className="space-y-4">
                  <div className="p-4 border border-dashed border-gray-200 dark:border-gray-800 rounded-xl text-center space-y-2 bg-gray-50/50 dark:bg-gray-900/30">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls,.csv,.txt"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-xl">📊</span>
                      <p className="text-xs text-gray-700 dark:text-gray-200 font-medium">
                        {csvFileName
                          ? `Active File: ${csvFileName}`
                          : "Upload an Excel (.xlsx, .xls) or CSV file with your contact & variable data"}
                      </p>
                    </div>
                    {csvFileName && (
                      <p className="text-[11px] text-gray-400">
                        Found {uploadedFileColumns.length} columns and {uploadedRawRows.length} data rows
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="px-4 py-2 rounded-xl bg-[#3a57e8] hover:bg-blue-600 text-white text-xs font-semibold shadow-xs transition"
                    >
                      {csvFileName ? "Choose Different Excel / CSV File" : "Select CSV / Excel File"}
                    </button>
                  </div>

                  {/* Match Variables to Excel Columns Panel */}
                  {uploadedFileColumns.length > 0 && (
                    <div className="p-4 rounded-2xl bg-white dark:bg-[#151824] border border-blue-200 dark:border-blue-900/60 shadow-xs space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-100 dark:border-gray-800 pb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">🔀</span>
                          <div>
                            <h4 className="text-xs font-bold text-gray-900 dark:text-white flex items-center gap-2">
                              Match Variables to Excel Columns
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                                {recipients.length} Recipients Ready
                              </span>
                            </h4>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400">
                              Select which column in your Excel file corresponds to each template variable.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Recipient Contact Fields */}
                      <div className="space-y-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                          1. Recipient Contact Info
                        </span>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {/* Phone Number */}
                          <div className={`p-3 rounded-xl border transition ${columnMapping.phone ? "bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800/80" : "bg-red-50/50 dark:bg-red-950/20 border-red-300 dark:border-red-800/80"}`}>
                            <div className="flex items-center justify-between text-xs mb-1.5">
                              <span className="font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                                <span>📱</span> Phone Number <span className="text-red-500">*</span>
                              </span>
                              <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                                Required
                              </span>
                            </div>
                            <select
                              value={columnMapping.phone || ""}
                              onChange={(e) => handleMappingChange("phone", e.target.value)}
                              className="w-full px-2.5 py-1.5 text-xs bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white font-mono focus:border-[#3a57e8] focus:outline-none"
                            >
                              <option value="">-- Select Phone Column --</option>
                              {uploadedFileColumns.map((col) => (
                                <option key={col} value={col}>
                                  Column: {col} {sampleRow[col] ? `(e.g. "${sampleRow[col]}")` : ""}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Recipient Name */}
                          <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800">
                            <div className="flex items-center justify-between text-xs mb-1.5">
                              <span className="font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                                <span>👤</span> Recipient Name
                              </span>
                              <span className="text-[10px] text-gray-400">Optional</span>
                            </div>
                            <select
                              value={columnMapping.name || ""}
                              onChange={(e) => handleMappingChange("name", e.target.value)}
                              className="w-full px-2.5 py-1.5 text-xs bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white font-mono focus:border-[#3a57e8] focus:outline-none"
                            >
                              <option value="">-- Auto-detect / None --</option>
                              {uploadedFileColumns.map((col) => (
                                <option key={col} value={col}>
                                  Column: {col} {sampleRow[col] ? `(e.g. "${sampleRow[col]}")` : ""}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* WhatsApp Meta Body Variables */}
                      {metaBodyVariables.length > 0 && (
                        <div className="space-y-2 pt-1">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                              2. WhatsApp Message Body Variables ({metaBodyVariables.length})
                            </span>
                            <span className="text-[10px] text-gray-400">Sent in WhatsApp text</span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {metaBodyVariables.map((mv) => {
                              const isMapped = !!columnMapping[mv];
                              return (
                                <div
                                  key={`meta_${mv}`}
                                  className={`p-3 rounded-xl border transition ${
                                    isMapped
                                      ? "bg-blue-50/40 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800/80"
                                      : "bg-red-50/40 dark:bg-red-950/20 border-red-300 dark:border-red-800/80"
                                  }`}
                                >
                                  <div className="flex items-center justify-between text-xs mb-1.5">
                                    <span className="font-mono font-bold text-blue-700 dark:text-blue-300">
                                      {"{{" + mv + "}}"} [WhatsApp Body]
                                    </span>
                                    {isMapped ? (
                                      <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold">✓ Mapped</span>
                                    ) : (
                                      <span className="text-[10px] text-red-500 font-semibold">Unmapped</span>
                                    )}
                                  </div>
                                  <select
                                    value={columnMapping[mv] || ""}
                                    onChange={(e) => handleMappingChange(mv, e.target.value)}
                                    className="w-full px-2.5 py-1.5 text-xs bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white font-mono focus:border-[#3a57e8] focus:outline-none"
                                  >
                                    <option value="">-- Choose Excel Column for {"{{" + mv + "}}"} --</option>
                                    {uploadedFileColumns.map((col) => (
                                      <option key={col} value={col}>
                                        Column: {col} {sampleRow[col] ? `(e.g. "${sampleRow[col]}")` : ""}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Canvas Dynamic Image Graphic Variables */}
                      {canvasImageVariables.length > 0 && (
                        <div className="space-y-2 pt-1">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400">
                              3. Canvas Graphic Variables ({canvasImageVariables.length})
                            </span>
                            <span className="text-[10px] text-gray-400">Rendered onto dynamic image</span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {canvasImageVariables.map((cv) => {
                              const isMapped = !!columnMapping[cv];
                              return (
                                <div
                                  key={`canvas_${cv}`}
                                  className={`p-3 rounded-xl border transition ${
                                    isMapped
                                      ? "bg-purple-50/40 dark:bg-purple-950/20 border-purple-200 dark:border-purple-800/80"
                                      : "bg-red-50/40 dark:bg-red-950/20 border-red-300 dark:border-red-800/80"
                                  }`}
                                >
                                  <div className="flex items-center justify-between text-xs mb-1.5">
                                    <span className="font-mono font-bold text-purple-700 dark:text-purple-300">
                                      {"{{" + cv + "}}"} [Image Graphic]
                                    </span>
                                    {isMapped ? (
                                      <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold">✓ Mapped</span>
                                    ) : (
                                      <span className="text-[10px] text-red-500 font-semibold">Unmapped</span>
                                    )}
                                  </div>
                                  <select
                                    value={columnMapping[cv] || ""}
                                    onChange={(e) => handleMappingChange(cv, e.target.value)}
                                    className="w-full px-2.5 py-1.5 text-xs bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white font-mono focus:border-[#3a57e8] focus:outline-none"
                                  >
                                    <option value="">-- Choose Excel Column for {"{{" + cv + "}}"} --</option>
                                    {uploadedFileColumns.map((col) => (
                                      <option key={col} value={col}>
                                        Column: {col} {sampleRow[col] ? `(e.g. "${sampleRow[col]}")` : ""}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Contact Group */}
              {audienceMode === "group" && (
                <div>
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                    Select Contact Group
                  </label>
                  <select
                    value={selectedGroupId}
                    onChange={(e) => setSelectedGroupId(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-xs text-gray-900 dark:text-white"
                  >
                    <option value="">Choose a contact group...</option>
                    {groups.map((g) => (
                      <option key={g._id} value={g._id}>
                        {g.str_group_name || g.name} ({g.int_contact_count || 0} contacts)
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* STEP 4: Scheduling */}
            <div className="bg-white dark:bg-[#151824] p-5 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-xs space-y-3">
              <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
                <span className="w-5 h-5 rounded-full bg-[#3a57e8] text-white flex items-center justify-center text-[10px]">
                  4
                </span>
                <span>Delivery Schedule</span>
              </div>

              <div className="flex items-center gap-4 pt-1">
                <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-gray-700 dark:text-gray-300">
                  <input
                    type="radio"
                    name="timing"
                    checked={!isScheduled}
                    onChange={() => setIsScheduled(false)}
                    className="text-[#3a57e8] focus:ring-0"
                  />
                  <span>Send Immediately</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-gray-700 dark:text-gray-300">
                  <input
                    type="radio"
                    name="timing"
                    checked={isScheduled}
                    onChange={() => setIsScheduled(true)}
                    className="text-[#3a57e8] focus:ring-0"
                  />
                  <span>Schedule for Later</span>
                </label>
              </div>

              {isScheduled && (
                <div className="pt-2">
                  <input
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    className="px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-xs text-gray-900 dark:text-white"
                  />
                </div>
              )}
            </div>
          </div>

          {/* RIGHT COLUMN: Live WhatsApp Message Preview with Dynamic Image & Body (5 cols) */}
          <div className="lg:col-span-5 space-y-5">
            <div className="bg-white dark:bg-[#151824] p-5 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-xs space-y-4 sticky top-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
                    WhatsApp Message Preview
                  </h3>
                  <span className="text-[10px] text-gray-400">
                    Real-time recipient preview with dynamic header
                  </span>
                </div>
                <span className="text-[10px] bg-emerald-500/10 text-emerald-600 font-bold px-2 py-0.5 rounded-full">
                  Live Canvas Render
                </span>
              </div>

              {/* Recipient Switcher */}
              {recipients.length > 0 && (
                <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-900 p-2 rounded-xl border border-gray-200 dark:border-gray-800 text-xs">
                  <button
                    disabled={previewRecipientIdx <= 0}
                    onClick={() => setPreviewRecipientIdx((prev) => Math.max(0, prev - 1))}
                    className="p-1 px-2 rounded-lg bg-white dark:bg-gray-800 border text-gray-600 dark:text-gray-300 disabled:opacity-30"
                  >
                    ◀ Prev
                  </button>
                  <span className="font-semibold text-gray-700 dark:text-gray-300 text-[11px]">
                    Recipient {previewRecipientIdx + 1} of {recipients.length} ({recipients[previewRecipientIdx]?.phone})
                  </span>
                  <button
                    disabled={previewRecipientIdx >= recipients.length - 1}
                    onClick={() => setPreviewRecipientIdx((prev) => Math.min(recipients.length - 1, prev + 1))}
                    className="p-1 px-2 rounded-lg bg-white dark:bg-gray-800 border text-gray-600 dark:text-gray-300 disabled:opacity-30"
                  >
                    Next ▶
                  </button>
                </div>
              )}

              {/* WHATSAPP BUBBLE PREVIEW */}
              <div className="p-4 bg-[#e5ddd5] dark:bg-[#0b141a] rounded-2xl border border-[#d1d7db] dark:border-gray-800 shadow-inner flex flex-col items-center">
                <div className="max-w-[340px] w-full bg-white dark:bg-[#1f2c34] rounded-2xl shadow-md overflow-hidden border border-black/5 flex flex-col">
                  {/* Dynamic Rendered Canvas Image (Image Header) */}
                  <div className="w-full bg-slate-900 aspect-square flex items-center justify-center overflow-hidden relative">
                    {renderingPreview ? (
                      <div className="text-xs text-slate-400 py-12 flex flex-col items-center gap-2">
                        <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                        <span>Rendering personalized graphic...</span>
                      </div>
                    ) : samplePreviewUrl ? (
                      <img
                        src={samplePreviewUrl}
                        alt="Dynamic Render"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-xs text-slate-500 text-center p-4">
                        Pick a canvas design to preview image
                      </div>
                    )}
                  </div>

                  {/* WhatsApp Template Body Text */}
                  <div className="p-3.5 space-y-2 text-xs">
                    <p className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap leading-relaxed">
                      {selectedMetaTemplate?.bodyText
                        ? replacePlaceholders(
                            selectedMetaTemplate.bodyText,
                            recipients[previewRecipientIdx]?.template_variables ||
                              recipients[previewRecipientIdx]?.variables ||
                              {}
                          )
                        : "Approved WhatsApp Template Body Text"}
                    </p>

                    {/* Footer Text */}
                    {selectedMetaTemplate?.footerText && (
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 pt-1">
                        {selectedMetaTemplate.footerText}
                      </p>
                    )}

                    {/* Timestamp & Sent Checks */}
                    <div className="flex justify-end items-center gap-1 text-[10px] text-gray-400 pt-0.5">
                      <span>{new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                      <span className="text-[#53bdeb] font-bold">✓✓</span>
                    </div>
                  </div>

                  {/* Buttons */}
                  {Array.isArray(selectedMetaTemplate?.buttons) && selectedMetaTemplate.buttons.length > 0 && (
                    <div className="border-t border-gray-100 dark:border-gray-700/60 divide-y divide-gray-100 dark:divide-gray-700/60">
                      {selectedMetaTemplate.buttons.map((btn, bIdx) => (
                        <div
                          key={bIdx}
                          className="py-2.5 px-3 text-center text-xs font-semibold text-[#00a884] dark:text-[#00a884] hover:bg-gray-50 dark:hover:bg-gray-800 transition"
                        >
                          {btn.text || btn.url || `Action Button ${bIdx + 1}`}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Parameter Routing Summary for Current Preview Recipient */}
              <div className="p-3 bg-gray-50 dark:bg-gray-900/60 rounded-xl border border-gray-200 dark:border-gray-800 space-y-2 text-[11px]">
                <div className="font-semibold text-gray-700 dark:text-gray-300 flex items-center justify-between">
                  <span>Parameter Routing Breakdown</span>
                  <span className="text-[10px] text-gray-400">Recipient #{previewRecipientIdx + 1}</span>
                </div>
                <div className="space-y-1">
                  <div className="text-gray-500">
                    <strong className="text-blue-600 dark:text-blue-400">WhatsApp Body ({metaBodyVariables.length}):</strong>{" "}
                    {metaBodyVariables.length === 0 ? (
                      <span className="italic">No body variables expected</span>
                    ) : (
                      metaBodyVariables.map((v) => (
                        <span key={v} className="inline-block bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded mr-1 font-mono">
                          {"{{" + v + "}}"} = "{recipients[previewRecipientIdx]?.template_variables?.[v] || recipients[previewRecipientIdx]?.variables?.[v] || ""}"
                        </span>
                      ))
                    )}
                  </div>
                  <div className="text-gray-500">
                    <strong className="text-purple-600 dark:text-purple-400">Graphic Canvas ({canvasImageVariables.length}):</strong>{" "}
                    {canvasImageVariables.length === 0 ? (
                      <span className="italic">No canvas variables</span>
                    ) : (
                      canvasImageVariables.map((v) => (
                        <span key={v} className="inline-block bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded mr-1 font-mono">
                          {"{{" + v + "}}"} = "{recipients[previewRecipientIdx]?.image_variables?.[v] || recipients[previewRecipientIdx]?.all_variables?.[v] || ""}"
                        </span>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Progress bar during sending */}
              {isSending && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Rendering graphics & dispatching...</span>
                    <span>{sendProgress}%</span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                    <div
                      className="h-full bg-[#3a57e8] transition-all duration-300"
                      style={{ width: `${sendProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Launch CTA Button */}
              <button
                onClick={handleLaunchCampaign}
                disabled={isSending || recipients.length === 0 || !selectedMetaTemplate || !selectedDynamicTemplate}
                className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-[#3a57e8] to-blue-600 hover:from-blue-600 hover:to-[#3a57e8] text-white text-xs font-bold shadow-lg shadow-[#3a57e8]/30 transition disabled:opacity-50 cursor-pointer flex items-center justify-center gap-2"
              >
                {isSending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Processing {recipients.length} Images...</span>
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon className="w-4 h-4" />
                    <span>Launch Image Camp ({recipients.length})</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* CAMPAIGN HISTORY TAB */
        <div className="bg-white dark:bg-[#151824] rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden shadow-xs space-y-4 p-5">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white">
              Dynamic Image Campaigns
            </h3>
            <button
              onClick={fetchCampaignHistory}
              className="p-1.5 text-gray-400 hover:text-[#3a57e8] rounded-lg transition"
              title="Refresh History"
            >
              <ArrowPathIcon className={`w-4 h-4 ${loadingHistory ? "animate-spin" : ""}`} />
            </button>
          </div>

          {loadingHistory ? (
            <div className="py-12 text-center text-xs text-gray-400">Loading campaign logs...</div>
          ) : historyList.length === 0 ? (
            <div className="py-16 text-center text-xs text-gray-400">
              No Image Camp broadcasts launched yet. Click "New Campaign" to send your first!
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 dark:bg-gray-900/60 text-gray-400 uppercase tracking-wider font-semibold border-b border-gray-100 dark:border-gray-800">
                  <tr>
                    <th className="px-4 py-3">Campaign</th>
                    <th className="px-4 py-3">Meta Template</th>
                    <th className="px-4 py-3">Canvas Design</th>
                    <th className="px-4 py-3">Sender</th>
                    <th className="px-4 py-3">Recipients</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Created</th>
                    <th className="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {historyList.map((c) => (
                    <tr key={c._id || c.str_campaign_id} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/40">
                      <td className="px-4 py-3">
                        <div className="font-bold text-gray-900 dark:text-white">
                          {c.str_campaign_name}
                        </div>
                        <div className="text-[10px] text-gray-400 font-mono">
                          {c.str_campaign_id}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono font-bold text-blue-600 dark:text-blue-400 text-[11px]">
                          {c.str_template_name}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                        {c.str_dynamic_template_name || c.obj_template_data?.dynamic_template_name || "-"}
                      </td>
                      <td className="px-4 py-3 font-mono text-gray-600 dark:text-gray-300">
                        {c.str_sender_number || c.str_phone_number_id || "-"}
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-200">
                        {c.int_recipient_count || c.arr_recipients?.length || 0}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            c.str_status === "COMPLETED"
                              ? "bg-emerald-500/10 text-emerald-500"
                              : c.str_status === "SCHEDULED"
                              ? "bg-amber-500/10 text-amber-500"
                              : c.str_status === "FAILED"
                              ? "bg-rose-500/10 text-rose-500"
                              : "bg-blue-500/10 text-blue-500 animate-pulse"
                          }`}
                        >
                          {c.str_status || "PROCESSING"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-[11px]">
                        {c.dttm_created_on ? new Date(c.dttm_created_on).toLocaleString() : "-"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => navigate("/workspace/reports/campaigns")}
                          className="text-[#3a57e8] hover:underline text-xs font-semibold"
                        >
                          View Report
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageCampaign;
